import { globalStores } from '@/editor/stores';
import { ELayoutType, IFullLayoutResultData } from '@/service/layout/types';
import { getUniqueId } from '..';
import { EDoorWindowType, IWindowData } from '@/editor/data/model/model';
import { DEFAULT_DOOR_OFFGROUND } from '@/editor/constant/entity';
import { createSeparateWallData } from '@/editor/data/separateWall/utils';
import { getRoomId } from '@/editor/data/room/utils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { wallAdsorbDistance } from '@/editor/math/const';
import { calculatePolygonArea } from '@/editor/model/polygon/utils';
import { booleanPolygon, BooleanPolygonType } from '@/editor/model/polygon';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { REGION_NAME_BY_TYPE } from '@/editor/controller/editParams/const';
import { ERegionType, ERoomType } from '@/editor/data/type';
import { modelManager } from '@/editor/manager/entityManager';
import { EEntityType, EFaceSide } from '@/editor/types';
import { convertLayoutWallTypeToSeparateWallType } from './utils';
import { DEFAULT_FACE_Material_BY_TYPE } from './constant';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { EWallFaceLayout } from '@/editor/data/face/face';
import { drawPreview } from '@/utils/development/layout';
import { IDrawSegParams } from '@/pages/development/layout-task/utils/payload';
import { FLAGS } from '@/flags';

export const parseFurniture = (data: IFullLayoutResultData['outPutMessage'][number]['furnitureDatas'][number]) => {
  const modelInfo = globalStores.modelStore.getModel(data.modelId);

  if (!modelInfo) {
    console.warn(`LAYOUT APPLY: model not found: ${data.modelId}`);
    return undefined;
  }

  return {
    id: getUniqueId(),
    modelId: modelInfo.id,
    center: data.center,
    width: data.width,
    thickness: data.height,
    height: modelInfo.properties.height,
    offGround: 0,
    rotation: data.rotation
  };
};

export const parseDoorWindow =
  (separateWallMap: Map<string, string>) =>
  (data: IFullLayoutResultData['outPutMessage'][number]['doorWindowDatas'][number]) => {
    const relativeWallId = separateWallMap.get(data.relateWallIds);
    if (!relativeWallId) {
      console.log(`LAYOUT APPLY: relative wall not found for door/window: ${data.id}`);
      return undefined;
    }
    const windowData: IWindowData = {
      id: getUniqueId(),
      type: EDoorWindowType.singleDoor, // TODO: 目前只生成单开门
      modelId: globalStores.modelStore.getWindowDoorDefaultBrandGoodId(EDoorWindowType.singleDoor),
      width: data.width,
      height: data.height,
      offGround: DEFAULT_DOOR_OFFGROUND,
      distance: data.distance,
      center: data.center,
      rotation: 0,
      relativeWallInfo: {
        relativeWallId,
        wallType: EEntityType.SeparateWall
      },
      flip: data.flip,
      side: data.side,
      thickness: data.thickness
    };
    return windowData;
  };

export const parseSeparateWall =
  (roomId: string, separateWallMap: Map<string, string>) =>
  (data: IFullLayoutResultData['outPutMessage'][number]['separateWallDatas'][number]) => {
    const start = data.start;
    const end = data.end;

    const separateWallData = createSeparateWallData({
      roomId,
      start,
      end,
      thickness: data.thickness,
      height: data.height,
      offGround: 0,
      type: convertLayoutWallTypeToSeparateWallType(data.type)
    });

    separateWallMap.set(data.id, separateWallData.id);

    return separateWallData;
  };

/**
 * 将算法返回的隔墙数据端点对齐到房间内轮廓上，保证可以生成正常的隔墙
 */
export function alignRoundedPointToRoomPath(data: IFullLayoutResultData['outPutMessage'][number]['separateWallDatas']) {
  const roomData = getInvolvedRoomDatas(data)[0];

  if (!roomData) {
    console.log('没有找到相关房间数据', data);
    return data.map((item) => ({
      ...item,
      start: roundPoint(item.start),
      end: roundPoint(item.end)
    }));
  }

  const roomWallSegs = roomData.pathInfos.map((info) => {
    return new LineSeg2d(info.start, info.end);
  });
  const roomHoleWallSegs = roomData.holeInfos.map((hole) => hole.map((info) => new LineSeg2d(info.start, info.end)));

  // 过滤掉在房间轮廓外或者洞内的隔墙
  const filteredData = data.filter((item) => {
    const seg = new LineSeg2d(item.start, item.end);
    return isSegInPolygon(seg, roomWallSegs, roomHoleWallSegs);
  });

  roomWallSegs.push(...roomHoleWallSegs.flat());

  return filteredData.map((item) => {
    // 点可能是浮点数
    const originStart = roundPoint(item.start);
    const originEnd = roundPoint(item.end);
    const result = { ...item, start: originStart, end: originEnd };
    const originSeg = new LineSeg2d(originStart, originEnd);
    const expandedSeg = getExpandedLineSeg(originStart, originEnd);
    let isStartAlign = false;
    let isEndAlign = false;
    // 检测与房间墙体的交点
    for (const wallSeg of roomWallSegs) {
      if (isStartAlign && isEndAlign) break;
      const originIntersect = wallSeg.intersection(originSeg);
      const expandedIntersect = wallSeg.intersection(expandedSeg);

      if (originIntersect) {
        // 如果原始线段与墙体相交，则可以采取直接裁切，不考虑吸附距离
        const startDistance = Point2dUtils.distanceTo(originIntersect, originStart);
        const endDistance = Point2dUtils.distanceTo(originIntersect, originEnd);
        if (!isStartAlign && startDistance <= endDistance) {
          result.start = originIntersect;
          isStartAlign = true;
        }

        if (!isEndAlign && endDistance <= startDistance) {
          result.end = originIntersect;
          isEndAlign = true;
        }
      }

      if (!originIntersect && expandedIntersect) {
        if (!isStartAlign) {
          const startDistance = Point2dUtils.distanceTo(expandedIntersect, originStart);
          if (startDistance <= wallAdsorbDistance) {
            result.start = expandedIntersect;
            isStartAlign = true;
          }
        }

        if (!isEndAlign) {
          const endDistance = Point2dUtils.distanceTo(expandedIntersect, originEnd);
          if (endDistance <= wallAdsorbDistance) {
            result.end = expandedIntersect;
            isEndAlign = true;
          }
        }
      }
    }
    return result;
  });
}

/**
 * 获取布局隔墙数据所在的房间列表
 */
function getInvolvedRoomDatas(data: IFullLayoutResultData['outPutMessage'][number]['separateWallDatas']) {
  const roomDatas = globalStores.roomStore.getRoomDatas();

  const roomIds = data
    .flatMap((item) => {
      const midPoint = new LineSeg2d(item.start, item.end).getSegmentCenter();
      return getRoomId(roomDatas, midPoint);
    })
    .filter((id): id is string => id !== undefined);
  return Array.from(new Set(roomIds))
    .map((id) => globalStores.roomStore.getRoomData(id))
    .filter((data) => data !== undefined);
}

/**
 * 根据起点和终点，获取经过扩展后的 LineSeg2d
 */
function getExpandedLineSeg(start: IPoint2d, end: IPoint2d): LineSeg2d {
  const dir = { x: end.x - start.x, y: end.y - start.y };
  const length = Math.sqrt(dir.x * dir.x + dir.y * dir.y);
  const normDir = { x: dir.x / length, y: dir.y / length };

  // 用最小吸附距离扩展线段
  const expandedStart = {
    x: start.x - normDir.x * wallAdsorbDistance,
    y: start.y - normDir.y * wallAdsorbDistance
  };
  const expandedEnd = {
    x: end.x + normDir.x * wallAdsorbDistance,
    y: end.y + normDir.y * wallAdsorbDistance
  };

  return new LineSeg2d(expandedStart, expandedEnd);
}

/**
 * 将点坐标四舍五入为整数
 */
function roundPoint(point: IPoint2d): IPoint2d {
  return {
    x: Math.round(point.x),
    y: Math.round(point.y)
  };
}

function calculatePolygonSetArea(polygons: IPoint2d[][]): number {
  return Math.abs(polygons.reduce((sum, polygon) => sum + Math.abs(calculatePolygonArea(polygon)), 0));
}

function calculateIntersectionRatio(main: IPoint2d[][], assistant: IPoint2d[][]) {
  const intersections = booleanPolygon({ main, assistant, type: BooleanPolygonType.Intersect });
  const intersectionArea = calculatePolygonSetArea(intersections);
  const mainArea = calculatePolygonSetArea(main);
  return { intersections, ratio: intersectionArea / mainArea };
}

function findBestMatchRegionIndex(
  availableRegionData: {
    isUsed: boolean;
    path: IPoint2d[];
  }[],
  path: IPoint2d[]
): number {
  let bestIndex = -1;
  for (let index = 0; index < availableRegionData.length; index++) {
    const info = availableRegionData[index];

    if (info.isUsed) {
      continue;
    }

    // 用实际的分区去和算法返回的分区路径做相交，计算重叠面积占实际分区面积的比例，比例最高且大于0.75的认为是最佳匹配
    // TODO: 在实际分区完全被算法分区包含但实际上并不属于同一分区的情况下，可能会出现匹配错误的问题。
    const { ratio } = calculateIntersectionRatio([path.map(roundPoint)], [info.path.map(roundPoint)]);

    if (ratio > 0.75) {
      bestIndex = index;
      availableRegionData[bestIndex].isUsed = true;
      break;
    }
  }
  return bestIndex;
}

export function restoreRegionInfo(
  data: IFullLayoutResultData['outPutMessage'][number]['regionDatas'],
  roomId: string,
  layoutType: ELayoutType
) {
  const roomData = globalStores.roomStore.getRoomData(roomId);

  if (!roomData) return;

  // 刷新房间的分区和面数据
  globalStores.regionStore.refreshRegionDatasByRoomId(roomData.id);
  globalStores.faceStore.refreshFaceDatas();

  // 获取刷新后房间内的分区数据
  const roomRegions = globalStores.regionStore.getRoomRegions(roomData.id);

  const previewData: IDrawSegParams = {
    title: '生成的分区预览',
    path: roomData.pathInfos,
    hole: [...roomRegions.map((region) => region.corePathInfos), ...roomData.holeInfos],
    doorWindows: [],
    column: [],
    direction: false
  };

  if (FLAGS.FEATURES_DEV.ENABLE_LAYOUT_DATA_PREVIEW) {
    drawPreview(previewData);
  }

  const validatedRegionData = data
    .map((data) => {
      if (data.separateWallDatas.length < 3) {
        console.warn('AUTO_LAYOUT: invalid region data with less than 3 walls, skip', data);
        return undefined;
      }
      const path = data.separateWallDatas.reduce<IPoint2d[]>((acc, wall) => {
        if (acc.length === 0) {
          acc.push(wall.start, wall.end);
        } else {
          const isExisting = acc.some((point) => Point2dUtils.equals(point, wall.end));
          if (!isExisting) {
            acc.push(wall.end);
          }
        }
        return acc;
      }, []);

      return { isUsed: false, type: data.regionInfo.type, path };
    })
    .filter((item) => item !== undefined);

  roomRegions
    // 面积较大的分区优先匹配，避免小分区被错误匹配导致大分区匹配失败
    // TODO: 后续可以让算法返回分区的开洞，以得到更精确的匹配结果
    .sort((a, b) => {
      const areaA = calculatePolygonArea(a.corePathInfos.map((info) => info.start));
      const areaB = calculatePolygonArea(b.corePathInfos.map((info) => info.start));
      return Math.abs(areaB) - Math.abs(areaA);
    })
    .forEach((regionData) => {
      const regionPath = regionData.corePathInfos.reduce<IPoint2d[]>((acc, wall) => {
        if (acc.length === 0) {
          acc.push(wall.start, wall.end);
        } else {
          const isExisting = acc.some((point) => Point2dUtils.equals(point, wall.end));
          if (!isExisting) {
            acc.push(wall.end);
          }
        }
        return acc;
      }, []);

      const index = findBestMatchRegionIndex(validatedRegionData, regionPath);

      if (index !== -1) {
        validatedRegionData[index].isUsed = true;
        const regionType = validatedRegionData[index].type;
        const name = REGION_NAME_BY_TYPE[regionType];
        const regionInfo = {
          ...regionData.regionInfo,
          ...validatedRegionData[index],
          name: name || REGION_NAME_BY_TYPE[ERegionType.Default]
        };
        globalStores.regionStore.updateRegionBaseData(regionData.id, {
          regionInfo
        });
        applyMaterialToRegion(regionData.id, regionType, layoutType);
      } else {
        console.warn(validatedRegionData, regionPath);
        const regionInfo = {
          ...regionData.regionInfo,
          type: ERegionType.OtherRoom,
          name: REGION_NAME_BY_TYPE[ERegionType.OtherRoom]
        };

        globalStores.regionStore.updateRegionBaseData(regionData.id, { regionInfo });
        applyMaterialToRegion(regionData.id, regionInfo.type, layoutType);
      }
    });

  const unusedRegion = validatedRegionData.filter((item) => !item.isUsed);

  modelManager.textInfoManager.update();
  return {
    previewData,
    unusedRegion
  };
}

/**
 * 为分区应用按类型应用材质
 */
function applyMaterialToRegion(id: string, type: ERegionType | ERoomType, layoutType: ELayoutType) {
  const material = DEFAULT_FACE_Material_BY_TYPE[layoutType]?.[type] ?? {
    wall: layoutType === ELayoutType.ABW ? 'WF-2' : 'WF-1',
    floor: 'FF-2'
  };

  const region = globalStores.regionStore.getRegionData(id);

  if (!material || !region) {
    return false;
  }

  if (material.floor) {
    globalStores.regionStore.updateBaseData(id, {
      materialInfo: { id: material.floor }
    });
    modelManager.regionManager.updateEntity(id);
  }

  if (material.wall) {
    globalStores.faceStore
      .getSepWallFaceDatas()
      .filter((faceData) => faceData.relativeInfo.regionId === id)
      .forEach((faceData) => {
        if (faceData.relativeInfo.sepType === ESeparateWallType.GlassWall) {
          return;
        }
        if (
          faceData.relativeInfo.sepType === ESeparateWallType.HalfGlassWall &&
          faceData.relativeInfo.layout === EWallFaceLayout.MIDDLE
        ) {
          return;
        }

        globalStores.faceStore.updateFaceMaterial(faceData.id, {
          id: material.wall
        });
      });

    const columnIds = globalStores.structStore
      .getColumnDatasInPathInfos(region.corePathInfos, region.coreHoleInfos, 'center')
      .map((column) => column.id);

    globalStores.faceStore
      .getColumnFaceDatas()
      .filter(
        (faceData) =>
          faceData.relativeInfo.columnId !== undefined &&
          faceData.relativeInfo.columnSide !== undefined &&
          columnIds.includes(faceData.relativeInfo.columnId) &&
          [
            EFaceSide.CylinderSide,
            EFaceSide.MainLeftSide,
            EFaceSide.MainRightSide,
            EFaceSide.StartSide,
            EFaceSide.EndSide
          ].includes(faceData.relativeInfo.columnSide)
      )
      .forEach((faceData) => {
        globalStores.faceStore.updateFaceMaterial(faceData.id, {
          id: material.wall
        });
      });
  }

  modelManager.faceManager.update();
}

/**
 * 清除分区内的家具
 *
 * @todo 清除分区内的独立墙体
 * @param regionId
 * @returns 是否成功
 */
export function clearRegion(regionId: string): boolean {
  const regionData = globalStores.regionStore.getRegionData(regionId);

  if (!regionData) {
    console.warn('LAYOUT APPLY: region not found: ' + regionId);
    return false;
  }

  // 清除分区内家具
  globalStores.modelStore
    .getFurnitureInPathInfos(regionData.corePathInfos, regionData.coreHoleInfos)
    .forEach((furnitureData) => {
      globalStores.modelStore.removeFurnitureData(furnitureData.id);
    });

  // TODO: 清除分区内的独立隔墙

  return true;
}

function isSegInPolygon(seg: LineSeg2d, outline: LineSeg2d[], holes: LineSeg2d[][]): boolean {
  const startInside =
    Curve2dUtils.isPointInPolygon(
      seg.start,
      outline.map((info) => info.start)
    ) &&
    !holes.some((hole) =>
      Curve2dUtils.isPointInPolygon(
        seg.start,
        hole.map((holeInfo) => holeInfo.start)
      )
    );
  const endInside =
    Curve2dUtils.isPointInPolygon(
      seg.end,
      outline.map((info) => info.start)
    ) &&
    !holes.some((hole) =>
      Curve2dUtils.isPointInPolygon(
        seg.end,
        hole.map((holeInfo) => holeInfo.start)
      )
    );
  const intersectOutline = outline.some((wallSeg) => wallSeg.intersection(seg));
  const intersectHole = holes.some((hole) => hole.some((holeSeg) => holeSeg.intersection(seg)));

  return (startInside && endInside) || intersectOutline || intersectHole;
}
